// src/components/modals/CompletionModal.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, Linking, StyleSheet, useWindowDimensions, Platform, Animated, Share } from 'react-native';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import { colors, typography } from '../../styles/theme';
import { handleBuyTreat } from '../../utils/purchase';
import * as Clipboard from 'expo-clipboard';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import { LayerData } from '../../core/svgParser';
import { FontAwesome6 } from '@expo/vector-icons';
import MenuListItem from '../ui/MenuListItem';

interface CompletionModalProps {
  visible: boolean;
  puzzleNumber: number;
  puzzleTitle?: string;
  score: number;
  timeString: string;
  streak: number;
  shareText: string;
  layers: LayerData[];
  capturedLayers: { id: string; score: number }[];
  onExport: () => void;
  onPlayAgain: () => void;
  onClose: () => void;
  onBrowsePacks: () => void;
  isCustom?: boolean;
}

export default function CompletionModal({
  visible,
  puzzleNumber,
  puzzleTitle,
  score,
  timeString,
  streak,
  shareText,
  layers,
  capturedLayers,
  onExport,
  onPlayAgain,
  onClose,
  onBrowsePacks,
  isCustom = false
}: CompletionModalProps) {
  const { width } = useWindowDimensions();
  const isDesktop = width > 600;

  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const tooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animatedHeightsRef = useRef<Animated.Value[]>([]);

  const screenStyle = {
    container: {
      flexDirection: (isDesktop ? 'row' : 'column') as any,
      alignItems: (isDesktop ? 'center' : 'stretch') as any,
      gap: isDesktop ? 40 : 10,
      marginVertical: 10,
      ...(Platform.OS === 'web' && { zIndex: 'auto' as any }),
    },
    statsCol: {
      flex: isDesktop ? 1 : undefined,
      alignItems: (isDesktop ? 'flex-start' : 'center') as any,
      paddingLeft: isDesktop ? 32 : 0,
    },
    chartCol: {
      flex: isDesktop ? 1 : undefined,
      ...(Platform.OS === 'web' && { zIndex: 'auto' as any }),
    },
  };

  const sortedLayers = useMemo(() => {
    return [...layers].sort((a, b) => {
      const numA = parseInt(a.id.replace('cls-', ''), 10);
      const numB = parseInt(b.id.replace('cls-', ''), 10);
      if (Number.isNaN(numA) || Number.isNaN(numB)) {
        return a.id.localeCompare(b.id);
      }
      return numA - numB;
    });
  }, [layers]);

  const scoreById = useMemo(() => {
    return new Map<string, number>(
      capturedLayers.map(layer => [layer.id, Math.round(layer.score || 0)])
    );
  }, [capturedLayers]);

  // Ensure animated heights array matches the sortedLayers length
  if (animatedHeightsRef.current.length !== sortedLayers.length) {
    animatedHeightsRef.current = sortedLayers.map((_, i) => {
      return animatedHeightsRef.current[i] || new Animated.Value(0);
    });
  }

  useEffect(() => {
    let activeAnimation: Animated.CompositeAnimation | null = null;

    if (visible) {
      // Reset all values to 0
      animatedHeightsRef.current.forEach(val => val.setValue(0));

      // Build timing animations
      const animations = sortedLayers.map((_, index) => {
        const animValue = animatedHeightsRef.current[index];
        return Animated.timing(animValue, {
          toValue: 1,
          duration: 500,
          useNativeDriver: false,
        });
      });

      // Start staggered animations (each starts 80ms after the previous one)
      activeAnimation = Animated.stagger(80, animations);
      activeAnimation.start();
    } else {
      // Reset when not visible
      animatedHeightsRef.current.forEach(val => val.setValue(0));
      setIsCopied(false);
    }

    return () => {
      if (activeAnimation) {
        activeAnimation.stop();
      }
    };
  }, [visible]);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleBarPress = (id: string) => {
    setActiveTooltipId((prevId) => {
      const nextId = prevId === id ? null : id;
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
      if (nextId) {
        tooltipTimeoutRef.current = setTimeout(() => {
          setActiveTooltipId(null);
        }, 2000);
      }
      return nextId;
    });
  };

  const handleShare = async () => {
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && !navigator.share) {
        throw new Error('Web Share API not supported');
      }
      await Share.share({
        message: shareText,
      });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return;
      }
      try {
        await Clipboard.setStringAsync(shareText);
        setIsCopied(true);
        if (copyTimeoutRef.current) {
          clearTimeout(copyTimeoutRef.current);
        }
        copyTimeoutRef.current = setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } catch (clipboardError) {
        console.error('Failed to copy to clipboard:', clipboardError);
      }
    }
  };

  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={
        <Text style={modalStyles.headerText}>
          {puzzleTitle ? `${puzzleTitle} Complete!` : (isCustom ? 'Custom Puzzle Complete!' : `Piclo #${puzzleNumber} Complete!`)}
        </Text>
      }
      footer={(
        <ModalStickyFooter>
          <HoverButton
            onPress={onBrowsePacks}
            style={modalStyles.primaryButton}
            textStyle={modalStyles.primaryButtonText}
            hoverStyle={modalStyles.primaryButtonHover}
          >
            Browse More Puzzles
          </HoverButton>
        </ModalStickyFooter>
      )}
    >

      <View style={screenStyle.container}>
        <View style={screenStyle.statsCol}>

          <Text style={styles.resultsHeader}>Your Results:</Text>

          <View style={[styles.statsContainer, isDesktop && styles.statsContainerDesktop]}>
            <Text style={styles.statText}>{score}% color match</Text>
            <Text style={styles.statText}>{timeString} total playtime</Text>
            {!isCustom && (
              <Text style={styles.statText}>{streak}-day streak</Text>
            )}
          </View>
        </View>

        <View style={screenStyle.chartCol}>
          <View style={styles.layerBarchartContainer}>
            {sortedLayers.map((layer, index) => {
              const layerScore = scoreById.get(layer.id) ?? 0;
              return (
                <Pressable
                  key={`layer-bar-${layer.id}`}
                  onPress={() => handleBarPress(layer.id)}
                  style={[
                    styles.layerBarCol,
                    index < sortedLayers.length - 1 && styles.layerBarColSpacing
                  ]}
                >
                  {activeTooltipId === layer.id && (
                    <View style={styles.layerBarTooltipContainer} pointerEvents="none">
                      <View style={styles.layerBarTooltipBox}>
                        <Text style={styles.layerBarTooltipText}>
                          Layer {index + 1}: {layerScore}% Match
                        </Text>
                      </View>
                      <View style={styles.layerBarTooltipArrow} />
                    </View>
                  )}
                  <View style={[styles.layerBarTrack]}>
                    <Animated.View
                      style={[
                        styles.layerBarFill,
                        {
                          height: animatedHeightsRef.current[index]
                            ? animatedHeightsRef.current[index].interpolate({
                              inputRange: [0, 1],
                              outputRange: ['0%', `${layerScore}%`]
                            })
                            : `${layerScore}%`,
                          backgroundColor: layer.targetHex
                        }
                      ]}
                    />
                  </View>

                </Pressable>
              );
            })}
          </View>
        </View>
      </View>

      <View style={styles.listGroup}>
        <MenuListItem
          icon={
            <FontAwesome6
              name={isCopied ? "check" : (Platform.OS === 'ios' ? "arrow-up-from-bracket" : "share-nodes")}
              size={18}
              color={colors.text}
              solid
            />
          }
          label={isCopied ? 'Copied to clipboard' : 'Share'}
          onPress={handleShare}
        />

        <MenuListItem
          icon={<FontAwesome6 name="arrow-up-from-bracket" size={18} color={colors.text} solid />}
          label="Export Artwork"
          onPress={onExport}
        />
        {/* <MenuListItem
          icon={<FontAwesome6 name="eye" size={18} color={colors.text} solid />}
          label="View Artwork"
          onPress={onClose}
        /> */}
        <MenuListItem
          icon={<FontAwesome6 name="rotate-right" size={18} color={colors.text} solid />}
          label="Restart Puzzle"
          onPress={onPlayAgain}
          isLast
        />
      </View>

      {/* <View style={styles.footer}>
        <Pressable onPress={handleBuyTreat}>
          <Text style={styles.link}>Buy Piccolo a treat 🦴</Text>
        </Pressable>
        <Text style={styles.footerText}>
          Created by <Text style={styles.link} onPress={() => Linking.openURL('https://jscottk.net')}>Scott Kuehnert</Text>.
        </Text>
      </View> */}

    </BaseModal>
  );
}

const styles = StyleSheet.create({
  headerText: {
    ...typography.header,
    textAlign: 'center',
    marginBottom: 0,
  },
  statsContainer: {
    alignItems: 'center',
  },
  statText: {
    ...typography.bodyBold,
    fontSize: 18,
    color: colors.text,
    marginBottom: 6,
  },
  resultsHeader: {
    fontFamily: 'JosefinSans_600SemiBold',
    fontSize: 20,
    color: colors.text,
    // marginLeft: 32, // 2rem
    marginBottom: 12,
  },
  statsContainerDesktop: {
    marginLeft: 32, // 4rem
    alignItems: 'flex-start',
  },
  layerBarchartContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'flex-end',
    height: 120,
    paddingHorizontal: 5,
    overflow: 'visible',
    ...(Platform.OS === 'web' && { zIndex: 'auto' as any }),
  },
  layerBarCol: {
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
    position: 'relative',
    overflow: 'visible',
    ...(Platform.OS === 'web' && { zIndex: 'auto' as any }),
  },
  layerBarColSpacing: {
    marginRight: 12,
  },
  layerBarTrack: {
    width: 12,
    flexGrow: 1,
    backgroundColor: colors.barBackground,
    borderRadius: 6,
    overflow: 'hidden',
    justifyContent: 'flex-end',
    marginBottom: 8,
  },
  layerBarFill: {
    width: '100%',
    borderRadius: 6,
  },
  layerBarSwatch: {
    width: 16,
    height: 16,
    borderRadius: 4,
  },
  layerBarSwatchCompact: {
    width: 12,
    height: 12,
  },
  layerBarTooltipContainer: {
    bottom: '100%', // <-- Pushes the tooltip to float directly ABOVE the bar
    marginBottom: 15, // Adds a little gap between the tooltip and the bar
    position: 'absolute',
    alignItems: 'center',

    // --- THE TIPPY.JS HACK ---
    width: 160,          // 1. Force a bounding box wide enough for the text
    left: '50%',         // 2. Anchor to the exact center of the 12px bar
    marginLeft: -80,     // 3. Pull it left by exactly half the width (160 / 2) to perfectly center it
    zIndex: 100,         // 4. Ensure it floats over adjacent bars
  },

  layerBarTooltipBox: {
    backgroundColor: '#333333',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    boxShadow: '0px 4px 15px rgba(51, 51, 51, 0.2)',
    elevation: 6,
  },
  layerBarTooltipText: {
    ...typography.body,
    color: colors.surface,
    fontSize: 13,
    fontWeight: '600',
    lineHeight: 16,
    textAlign: 'center',
  },
  layerBarTooltipArrow: {
    marginTop: -1,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderTopWidth: 6,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderTopColor: '#333333',
  },
  listGroup: {
    width: '100%',
    marginBottom: 16,
    position: 'relative',
    ...(Platform.OS === 'web' && { zIndex: 'auto' as any }),
  },
  footer: { alignItems: 'center', gap: 4 },
  footerText: { ...typography.body, fontSize: 16, color: colors.textMuted },
  link: { ...typography.body, fontSize: 16, color: colors.textMuted, textDecorationLine: 'underline' }
});
